import React, { useEffect, useState } from 'react';
import './UserProfile.css';
import privateAxios from '../../utils/axios/privateAxios';
import { FaUserCircle, FaEdit, FaUpload } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import Notification from '../../components/Notification/Notification';
import { jwtDecode } from 'jwt-decode';
import { Circles } from 'react-loader-spinner';

const allowedRoles = ['admin', 'events & media', 'editor', 'education', 'health', 'security', 'agriculture', 'wash', 'works', 'finance'];

const UserProfile = () => {
    const navigate = useNavigate();
    const [decodedData, setDecodedData] = useState({});
    const [userData, setUserData] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ type: '', message: '' });

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => {
            setNotification({ type: '', message: '' });
        }, 5000);
    };

    useEffect(() => {
        const token = localStorage.getItem('token');

        if (token) {
            try {
                const decoded = jwtDecode(token);
                setDecodedData(decoded);
            } catch (err) {
                console.error('Invalid token:', err);
                navigate('/login');
            }
        }
    }, [navigate]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const { data } = await privateAxios.get(`/private/user`);
                setUserData(data.user);
                setImagePreview(data.user.imageUrl || '');
            } catch (err) {
                console.error('Failed to fetch user', err);
                if (err.response?.status === 401 || err.response?.status === 403) {
                    navigate('/unauthorized');
                } else {
                    setError('An error occurred while loading your profile.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [navigate]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = async () => {
        if (!selectedImage) return;
        try {
            setUploading(true);
            const fileName = `${Date.now()}_${selectedImage.name}`;
            const { data: { url } } = await privateAxios.get(`/private/generate-upload-url`, {
                params: { filename: fileName }
            });

            await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': selectedImage.type },
                body: selectedImage,
            });

            const newImageUrl = url.split('?')[0];

            await privateAxios.put(`/private/edit-photo`, { newImageUrl });
            setUserData((prev) => ({ ...prev, imageUrl: newImageUrl }));
            setImagePreview(newImageUrl);
            setSelectedImage(null);
            showNotification('success', 'Your profile image uploaded successfully!');
        } catch (err) {
            console.error('Image upload failed', err);
            showNotification('error', 'Your profile image update failed!');
        } finally {
            setUploading(false);
        }
    };

    if (loading) { return (
        <div style={{ display: 'flex', columnGap: '10px', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', height: '300px', color: 'gray' }}>
            <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h2>Loading Your Profile Information...</h2>
        </div>);}
    if (error) return <div className="error-message">{error}</div>;
    if (!userData) return null;

    const userRoles = decodedData?.userRoles || [];

    return (
        <div className="user-profile">
            <Notification
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification({ type: '', message: '' })}
            />
            <div className="user-profile-wrapper">
                <h2>My Profile</h2>

                {allowedRoles.some(role => userRoles.includes(role)) && (
                    <button className="user-profile-admin-btn" onClick={() => navigate('/admin-dashboard')}>
                        Go to Dashboard
                    </button>
                )}

                <div className="user-profile-image-section">
                    {imagePreview ? (
                        <img src={imagePreview} alt="Profile" className="user-profile-image" />
                    ) : (
                        <FaUserCircle className="default-icon" size={130} />
                    )}
                    <label className="user-profile-edit-image-icon">
                        <FaEdit />
                        <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                    </label>
                    {selectedImage && (
                        <button className="upload-btn" onClick={handleImageUpload} disabled={uploading}>
                            {uploading ? 'Uploading...' : <><FaUpload /> Upload</>}
                        </button>
                    )}
                </div>

                <div className="user-profile-fields">
                    <ProfileRow label="Username" value={userData.username} />
                    <ProfileRow label="First Name" value={userData.firstname} />
                    <ProfileRow label="Last Name" value={userData.lastname} />
                    <ProfileRow label="Email" value={userData.email} />
                    <ProfileRow label="Phone Number" value={userData.phoneNumber} />
                    <ProfileRow label="Gender" value={userData.gender} />
                    <ProfileRow label="Date of Birth" value={userData.dateOfBirth?.substring(0, 10)} />
                    <ProfileRow label="Bio" value={userData.bio} />
                    <ProfileRow label="Status" value={userData.status} />
                    <ProfileRow label="Verified" value={String(userData.verified)} />
                    <ProfileRow label="Education Level" value={userData.educationLevel} />
                    <ProfileRow label="Occupation" value={userData.occupation} />
                    <ProfileRow label="Village" value={userData.village} />
                    <ProfileRow label="Town" value={userData.town} />
                    <ProfileRow label="State of Residence" value={userData.stateOfResidence} />
                </div>

                <Link to={`/edit-user/${userData._id}`} className="user-profile-edit-profile-link">
                    Edit Profile
                </Link>
            </div>
        </div>
    );
};

// Simple field row component
const ProfileRow = ({ label, value }) => (
    <div className="user-profile-field-row">
        <label>{label}:</label>
        <span>{value || 'N/A'}</span>
    </div>
);

export default UserProfile;